// ── 계약 모달 (등록/수정/신규직접등록/재주문) ────────────────────────────────
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useToast }    from '../../../components/Toast';
import QuickItemChips  from '../../../components/QuickItemChips';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import { buildInitForm } from './utils';
import {
  overlayStyle, modalBoxStyle, modalHeaderStyle, headerIconBox, modalCloseBtnStyle,
  modalBodyStyle, modalFooterStyle, cancelBtnStyle, saveBtnStyle,
  inputStyle,
} from './modalStyles';
import { stdModalHeaderBg } from '../adminlayout/designStandard';
import { ExistingPromoPanel } from './ExistingPromoPanel';
import { saveContract } from './contractSave';
import { validateWithConfig } from './contractValidate';
import { ContractModalSections } from './ContractModalBody';
import { NoProductConfirm } from './NoProductConfirm';
import { useContractApi } from './contractApi';
import { ContractCompanyPicker } from './ContractCompanyPicker';

export function ContractModal({ open, row, isNew, isReorder, isMobile, ticketTypes, externalCategories, allProductCategories, users, salesPersons, promotionalItemsList, formDesignCourier, fieldConfig = null, onClose, onSave }) {
  const { showToast } = useToast();
  const api = useContractApi(); // 주소 묶음(본사·회사별·통합) — 저장 주소를 여기서 정한다
  const [saving,            setSaving]            = useState(false);
  const [errors,            setErrors]            = useState({});
  const [chipsVisible,      setChipsVisible]      = useState(false);
  const [deliveryDirty,     setDeliveryDirty]     = useState(false);
  // 계약상품 미선택 확인 팝업
  const [noProductConfirm,  setNoProductConfirm]  = useState(false);
  const [autoFocusNew, setAutoFocusNew] = useState(false);
  const bodyRef = useRef(null);
  useBodyScrollLock(open);
  const overlayCloseProps = useOverlayClickClose(onClose);

  // 모달 열릴 때 칩 상태 초기화 + 스크롤 불필요한 경우 즉시 노출
  useEffect(() => {
    if (!open) return;
    setChipsVisible(false);
    const t = setTimeout(() => {
      const el = bodyRef.current;
      if (el && el.scrollHeight <= el.clientHeight + 4) setChipsVisible(true);
    }, 250);
    return () => clearTimeout(t);
  }, [open]);

  // 빠른 추가 — 발주량 상위 칩 클릭 시 promoItems에 즉시 추가
  function quickAddPromoItem(promoItem) {
    setForm((prev) => ({
      ...prev,
      promoItems: [...(prev.promoItems || []), {
        item_id:    promoItem.id,
        item_name:  promoItem.name,
        quantity:   '1',
        unit_price: promoItem.price || 0,
      }],
    }));
  }

  // initForm 메모이제이션 — props 변화 시 재계산
  const initForm = useCallback(
    () => buildInitForm({ isNew, isReorder, row, externalCategories, formDesignCourier }),
    [isNew, isReorder, row, externalCategories, formDesignCourier]
  );

  const [form, setForm] = useState(initForm);

  // 통합 계약 화면만(4-5, 결정 1-a): 직접 등록할 때 고를 회사들. 재주문이면 원래 계약 회사를 먼저 골라 두고, 새 등록은 비워 둔다.
  const pickCompanies = Array.isArray(api.createCompanies) ? api.createCompanies : null;
  function withCompanyPick(f) {
    if (!pickCompanies) return f;
    const prev = String(row?.company_code || '').trim().toUpperCase();
    return { ...f, contract_company_code: isReorder && pickCompanies.includes(prev) ? prev : '' };
  }

  // 모달 열릴 때 폼 초기화
  useEffect(() => {
    if (open) { setForm(withCompanyPick(initForm())); setErrors({}); setDeliveryDirty(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  function setField(key, val) {
    setForm((p) => ({ ...p, [key]: val }));
    // 값이 채워지면 에러 클리어
    setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
  }

  // 상품 칩 토글 — 선택 시 DB 기본 유효기간 자동 입력
  function setDeliveryForm(updater) {
    setDeliveryDirty(true);
    setForm(updater);
  }

  function toggleProduct(type, name) {
    const pKey = type === 'contract' ? 'contractProducts' : 'serviceProducts';
    const cKey = type === 'contract' ? 'contractCounts'   : 'serviceCounts';
    const vKey = type === 'contract' ? 'contractValidities' : 'serviceValidities';
    setForm((prev) => {
      const cur  = prev[pKey] || [];
      const on   = cur.includes(name);
      const next = on ? cur.filter((n) => n !== name) : [...cur, name];
      const nextCnts = { ...prev[cKey] };
      const nextVals = { ...prev[vKey] };
      if (on) {
        delete nextCnts[name];
        delete nextVals[name];
      } else {
        // 새 상품 선택 시 DB 기본 유효기간 자동 입력
        const product = ticketTypes.find((t) => (t.name || t) === name);
        if (product?.default_validity_months != null) {
          nextVals[name] = String(product.default_validity_months);
        }
      }
      return { ...prev, [pKey]: next, [cKey]: nextCnts, [vKey]: nextVals };
    });
    if (type === 'contract') setErrors((e) => { const n = { ...e }; delete n.contractProducts; delete n.contractCounts; return n; });
  }

  function setCount(type, name, val) {
    // 숫자만 허용
    const digits = String(val).replace(/[^0-9]/g, '');
    const cKey   = type === 'contract' ? 'contractCounts' : 'serviceCounts';
    setForm((prev) => ({ ...prev, [cKey]: { ...prev[cKey], [name]: digits } }));
    if (type === 'contract') setErrors((e) => { const n = { ...e }; delete n.contractCounts; return n; });
  }

  function setValidity(type, name, val) {
    const digits = String(val).replace(/[^0-9]/g, '');
    const vKey   = type === 'contract' ? 'contractValidities' : 'serviceValidities';
    setForm((prev) => ({ ...prev, [vKey]: { ...prev[vKey], [name]: digits } }));
  }

  // 실제 저장 실행 (확인 후 호출)
  // 실제 저장은 contractSave.js 로 분리했다(2026-09-22, 300줄 규칙). 순수 이동.
  async function doSave() {
    setNoProductConfirm(false);
    setSaving(true);
    const ok = await saveContract({
      form, row, isNew, isReorder, deliveryDirty,
      ticketTypes, allProductCategories, showToast, api,
    });
    setSaving(false);
    if (!ok) return;
    onSave();
    onClose();
  }

  // 저장 버튼 클릭 — 유효성 검사 후 상품 미선택 시 확인 팝업, 통과 시 doSave 호출
  function handleSave() {
    if (needsCompanyPick && !form.contract_company_code) {
      setErrors((p) => ({ ...p, contract_company_code: true }));
      showToast('등록할 회사를 골라 주세요.', 'error');
      return;
    }
    const { errors: e, labels } = validateWithConfig(form, fieldConfig, { isNew });
    if (Object.keys(e).length > 0) {
      setErrors(e);
      showToast(`필수 항목을 입력해 주세요: ${labels.join(', ')}`, 'error');
      return;
    }
    // 계약상품 미선택 시 확인 팝업 표시
    if (!form.contractProducts?.length) {
      setNoProductConfirm(true);
      return;
    }
    doSave();
  }

  // 렌더 변수 계산
  const title = isReorder
    ? `재주문 등록${row?.company_name ? ` — ${row.company_name}` : ''}`
    : isNew ? '신규 계약 직접 등록' : (row?.is_contracted ? '계약 수정' : '계약 등록');
  // 모달 헤더 검정 통일 (2026-08-31 사용자 확정) — 상황별 그라데이션 → 표준 검정(stdModalHeaderBg)
  const headerGradient = stdModalHeaderBg;
  const mobileFs = isMobile ? 9 : 11;
  // inp: 에러 있을 때 input 스타일
  const inp = (key) => ({
    ...inputStyle, fontSize: mobileFs,
    border:     `1px solid ${errors[key] ? '#ef4444' : 'var(--border)'}`,
    // #fff 하드코딩 금지 — 다크모드에서 흰 배경+어두운 글씨 충돌
    background: errors[key] ? 'rgba(239,68,68,0.08)' : 'var(--bg-card)',
  });
  // 모바일에서는 칩 패널 미노출 (화면 너비 부족). 회사별·통합 화면은 홍보물 보기만이라 칩도 없다(4-5, 결정 2-a).
  const promoWritable = api.promoWritable !== false;
  const canShowChips = !isMobile && window.innerWidth > 900;
  // 회사 고르기는 콜 없이 새로 등록할 때만 — 콜에 붙는 재주문·콜 계약 등록·수정은 서버가 콜·계약의 회사를 따른다(contractSave 와 같은 갈래)
  const needsCompanyPick = Boolean(pickCompanies?.length) && isNew
    && !(form.registrationType === 'reorder' && row?.tm_call_id);
  const existingPromoOrderId = !isNew ? (row?.promotional_order_id ?? null) : null;
  const addedPromoIds = (form.promoItems || []).map((i) => i.item_id).filter(Boolean);

  return (
    <div style={overlayStyle} {...overlayCloseProps}>
      {/* 모달 카드 */}
      <div style={modalBoxStyle} onClick={(e) => e.stopPropagation()}>

        {/* ── 헤더 ── */}
        <div style={{ ...modalHeaderStyle, background: headerGradient }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {!isReorder && (
              <div style={headerIconBox}>
                <span style={{ fontSize: 18 }}>{isNew ? '📋' : (row?.is_contracted ? '✏️' : '✅')}</span>
              </div>
            )}
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: '#fff', lineHeight: 1.3 }}>{title}</div>
              {!isNew && row?.company_name && (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>{row.company_name}</div>
              )}
            </div>
          </div>
          <button style={modalCloseBtnStyle} onClick={onClose}>✕</button>
        </div>

        {/* ── 바디 ── */}
        <div
          ref={bodyRef}
          style={modalBodyStyle}
          onScroll={(e) => {
            const el = e.currentTarget;
            if (!chipsVisible && el.scrollTop + el.clientHeight >= el.scrollHeight - 6) {
              setChipsVisible(true);
            }
          }}
        >
          {needsCompanyPick && (
            <ContractCompanyPicker
              companies={pickCompanies} value={form.contract_company_code || ''} isMobile={isMobile}
              error={Boolean(errors.contract_company_code)} onChange={(code) => setField('contract_company_code', code)}
            />
          )}
          <ContractModalSections
            promoWritable={promoWritable}
            cfg={fieldConfig} isNew={isNew} isReorder={isReorder} isMobile={isMobile}
            form={form} errors={errors} setField={setField} setForm={setForm} setDeliveryForm={setDeliveryForm}
            inp={inp} users={users} salesPersons={salesPersons} ticketTypes={ticketTypes}
            toggleProduct={toggleProduct} setCount={setCount} setValidity={setValidity}
            formDesignCourier={formDesignCourier} promotionalItemsList={promotionalItemsList}
            autoFocusNew={autoFocusNew} setAutoFocusNew={setAutoFocusNew}
            existingPromoOrderId={existingPromoOrderId}
            showInlinePromoPanel={Boolean(existingPromoOrderId) && !canShowChips}
          />
        </div>

        {/* ── 푸터 ── */}
        <div style={modalFooterStyle}>
          <button style={cancelBtnStyle} onClick={onClose}>취소</button>
          <button style={saveBtnStyle(isNew, row?.is_contracted)} disabled={saving} onClick={handleSave}>
            {saving ? '저장 중...' : isReorder ? '재주문 등록' : isNew ? '✓  계약 등록' : '변경 저장'}
          </button>
        </div>
      </div>

      {/* 계약상품 미선택 확인 팝업 */}
      {noProductConfirm && (
        <NoProductConfirm onCancel={() => setNoProductConfirm(false)} onConfirm={doSave} />
      )}

      {/* 기존 홍보물 패널 — position:fixed로 모달 왼쪽 바깥에 독립 배치 (수정 모달 전용) */}
      <ExistingPromoPanel
        promotionalOrderId={existingPromoOrderId}
        visible={Boolean(existingPromoOrderId) && canShowChips}
      />

      {/* 빠른 추가 칩 패널 — position:fixed로 모달 오른쪽 아래에 독립 배치 */}
      {chipsVisible && canShowChips && promoWritable && (
        <div
          style={{ position: 'fixed', left: 'calc(50% + 296px)', bottom: '8vh', zIndex: 2001 }}
          onClick={(e) => e.stopPropagation()}
        >
          <QuickItemChips
            items={promotionalItemsList}
            disabledIds={addedPromoIds}
            onSelect={quickAddPromoItem}
          />
        </div>
      )}
    </div>
  );
}
